import { useCallback, useEffect, useState } from 'react';
import { SlotInfo, View } from 'react-big-calendar';
import { useDispatch, useSelector } from 'react-redux';

import { getHours, isAfter, startOfDay } from 'date-fns';

import { AppDispatch } from 'app/store';

import { useInterviewSlots } from 'hooks/useInterviewSlots';

import { serializeSlot } from 'utils/dateUtils';

import {
  CalendarEvent,
  InterviewSlotSerialized,
  SlotType
} from 'types/interviewSlots';

import { InterviewSchedulerView } from 'constants/enums';

import {
  MAX_HOUR,
  MAX_SLOTS,
  MIN_HOUR,
  calendarSettings
} from '../config/calendarConfig';

interface UseSchedulerEventsProps {
  isMobile: boolean;
  maxSelectableDate: Date;
  isSlotDisabled: (date: Date) => boolean;
  proposedSlots?: InterviewSlotSerialized[];
}

export const useSchedulerEvents = ({
  isMobile,
  maxSelectableDate,
  isSlotDisabled,
  proposedSlots = []
}: UseSchedulerEventsProps) => {
  const dispatch: AppDispatch = useDispatch();
  const [lastSelectTime, setLastSelectTime] = useState<number>(0);

  const {
    slots,
    proposedSlots: reduxProposedSlots,
    selectedSlot,
    events,
    addSlot,
    removeSlot,
    selectProposedSlot,
    initProposedSlots
  } = useInterviewSlots();

  useEffect(() => {
    if (proposedSlots.length > 0) {
      // Filter out slots that are in the past
      const now = new Date();
      const validSlots = proposedSlots.filter((slot) => {
        const slotStart = new Date(slot.start);
        return isAfter(slotStart, now);
      });
      initProposedSlots(validSlots);
    }
  }, [proposedSlots, initProposedSlots]);

  const handleSelectEvent = useCallback(
    (event: CalendarEvent) => {
      if (event.type === SlotType.Selected) {
        // If this is a selected slot - remove it
        if (slots.some((slot) => slot.id === event.id)) {
          removeSlot(event.id);
        }
        // If this is a selected proposed slot - deselect it
        else if (event.id === selectedSlot?.id) {
          // Find slot in proposed and pass it
          const proposedSlot = reduxProposedSlots.find(
            (slot) => slot.id === event.id
          );
          if (proposedSlot) {
            selectProposedSlot(proposedSlot);
          }
        }
      } else if (event.type === SlotType.Proposed) {
        // If this is a proposed slot - select it
        // Find slot in proposed and pass it
        const proposedSlot = reduxProposedSlots.find(
          (slot) => slot.id === event.id
        );
        if (proposedSlot) {
          selectProposedSlot(proposedSlot);
        }
      }
    },
    [slots, selectedSlot, reduxProposedSlots, removeSlot, selectProposedSlot]
  );

  const handleSelectSlot = useCallback(
    (
      slotInfo: SlotInfo,
      currentView: View,
      onViewChange: (view: View) => void,
      onDateChange: (date: Date) => void
    ) => {
      // Allow view switching from month to week/day regardless of proposed slots
      if (currentView === InterviewSchedulerView.Month) {
        onDateChange(slotInfo.start);
        onViewChange(
          isMobile ? InterviewSchedulerView.Day : InterviewSchedulerView.Week
        );
        return;
      }

      // If there are proposed slots, you can only select from them (but allow view switching above)
      if (reduxProposedSlots.length > 0) {
        return;
      }

      if (isAfter(startOfDay(slotInfo.start), maxSelectableDate)) {
        return;
      }

      const now = Date.now();
      if (
        isMobile &&
        now - lastSelectTime < calendarSettings.mobileClickDelay
      ) {
        return;
      }
      setLastSelectTime(now);

      const startHour = getHours(slotInfo.start);
      const endHour = getHours(slotInfo.end);

      if (startHour < MIN_HOUR || startHour >= MAX_HOUR || endHour > MAX_HOUR) {
        return;
      }

      if (isSlotDisabled(slotInfo.start)) {
        return;
      }

      if (
        events.filter((e) => e.type === SlotType.Selected).length >= MAX_SLOTS
      ) {
        alert(`Maximum number of selected slots: ${MAX_SLOTS}`);
        return;
      }

      const start = new Date(slotInfo.start);
      start.setMinutes(0);
      start.setSeconds(0);
      start.setMilliseconds(0);
      const end = new Date(start.getTime() + 60 * 60 * 1000); // +1 hour

      // Check for overlapping slots
      const isOverlapping = events.some(
        (event) => event.start.getTime() === start.getTime()
      );

      if (isOverlapping) {
        return;
      }

      const newSlot = {
        id: Date.now(),
        start,
        end
      };

      addSlot(serializeSlot(newSlot));
    },
    [
      events,
      reduxProposedSlots.length,
      isMobile,
      isSlotDisabled,
      lastSelectTime,
      maxSelectableDate,
      addSlot
    ]
  );

  return {
    events,
    handleSelectEvent,
    handleSelectSlot
  };
};
